import cx from 'classnames';
import {TypeOptions} from 'react-toastify';
import urls from 'urls';
import {Step, StepConfig, StepMeta, Steps} from '~/QuickEcards/types/misc';
import {qsArrayTransform} from '~/CreateGreetingCard/utils/navigation';
import {AppStore} from '~/QuickEcards/store';
import {
  updateEcard,
  selectGlobalMessage,
  selectGiftCardProductConfigId,
} from '~/QuickEcards/store/reducers/ecard';
import {
  selectRecipientMessages,
  setIsMessageEditorOn,
  setIsMessagesControllerReady,
  updateGlobalMessage,
  updateSelectedTab,
} from '~/QuickEcards/store/reducers/recipientMessages';
import {checkIfContainsRecipients} from '~/common/fabric/EventGuests/api/guests';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {selectEventId} from '~/common/fabric/EventGuests/reducers/guests/selectors';
import {isEmpty} from '~/common/utils';
import {history} from '~/QuickEcards/history';
import {TOGGLE_E_GIFT_CARDS_DRAWER} from '~/common/fabric/GiftCard/reducers/eGiftCard';

const getGalleryUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);

  const category = urlParams.get('c');
  const page = urlParams.get('page');
  const filter = qsArrayTransform('filter');

  return category && page ? `/gallery/category/${category}/${page}?${filter}` : '/';
};

export const navigateToPrevious = (store: AppStore) => {
  const urlParams = new URLSearchParams(window.location.search);
  const previous = urlParams.get('previous');
  let destination = '/';
  const eCardId = store.getState().ecard.id;
  switch (previous) {
    case 'home':
      destination = '/';
      break;
    case 'event_dashboard':
      if (eCardId) destination = urls.get('event_dashboard', {event_id: eCardId});
      break;
    case 'gallery':
      destination = getGalleryUrl();
      break;
    default:
      break;
  }
  window.location.href = destination;
};

export const backToGallery = () => {
  window.location.href = getGalleryUrl();
};

export const setInEditMode = async (store: AppStore) => {
  store.dispatch(updateEcard({isInEditMode: true}));
  return true; // allow navigation
};

const showToastAlert = (title: string, message: string, toastType: TypeOptions = 'warning') => {
  const toastId = 'greeting-card__toast__text';
  // This will prevent showing multiple toasts for this action
  if (!document.getElementById(toastId)) {
    return toastAlert(
      <span>
        <Typography
          variant="paragraph2"
          classNames={cx('toast__message__title', 'quick-ecards__toast-text')}
          as="span"
        >
          {title}
        </Typography>
        <Typography as="span" variant="paragraph2" classNames="quick-ecards__toast-text">
          {message}
        </Typography>
      </span>,
      {
        type: toastType,
        icon: <AlertLineIcon />,
        position: 'top-right',
        autoClose: 2500,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        id: toastId,
      }
    );
  }
  return null;
};

export const closeGiftCardDrawer = async (store: AppStore) => {
  store.dispatch({
    type: TOGGLE_E_GIFT_CARDS_DRAWER,
    payload: !store.getState().ecard.event_data.gift_card_data.product_config_id,
  });
  return true;
};

export const validateRecipientListAndGlobalMessage = async (store: AppStore) => {
  const hasRecipients = await checkIfContainsRecipients(selectEventId(store.getState()));
  if (!hasRecipients) {
    showToastAlert('Missing recipients', 'Add recipients to your card in order to proceed.');
    return false;
  }
  const numGuests = store.getState().guests.guests.page.length;
  if (numGuests <= 1) {
    store.dispatch(updateGlobalMessage(''));
  }
  return true;
};

export const resetMessageEditor = async (store: AppStore) => {
  store.dispatch(updateSelectedTab(null));
  store.dispatch(setIsMessageEditorOn(false));
  store.dispatch(setIsMessagesControllerReady(false));
  return true;
};

export const disableMessageEditor = async (store: AppStore) => {
  store.dispatch(setIsMessageEditorOn(false));
  return true;
};

export const setGiftCardsNextButtonText = (store: AppStore) => {
  const productConfigId = selectGiftCardProductConfigId(store.getState());
  if (!productConfigId) return 'Skip & continue';
  return 'Next: Add recipient(s)';
};

export const setGiftCardsNextButtonVariant = (store: AppStore) => {
  const productConfigId = selectGiftCardProductConfigId(store.getState());
  return productConfigId ? 'primary' : 'secondary';
};

export const setRecipientsMessageNextButtonText = (store: AppStore) => {
  const globalMessage = selectGlobalMessage(store.getState());
  const recipientMessages = selectRecipientMessages(store.getState());

  const hasMessage =
    !isEmpty(globalMessage) ||
    Object.values(recipientMessages).some((message) => !isEmpty(message));

  if (!hasMessage) return 'Skip & check out';
  return 'Next: Review & send';
};

export const setRecipientsMessageNextButtonVariant = (store: AppStore) => {
  const globalMessage = selectGlobalMessage(store.getState());
  const recipientMessages = selectRecipientMessages(store.getState());

  const hasMessage =
    !isEmpty(globalMessage) ||
    Object.values(recipientMessages).some((message) => !isEmpty(message));

  return hasMessage ? 'primary' : 'secondary';
};

// export const setupWebviewNavigation = (store) => {
//   window.webview = window.webview || {};
//   window.webview.navigateToRoute = async (route: string) => navigateToRoute(route, store);
// };

export const eGiftCardsEnabled = !!window.fabricTemplate.create_flow_config?.egift_cards_enabled;

const createStep: StepConfig = {
  id: 1,
  name: Step.CUSTOMIZE,
  path: 'create',
  displayName: 'Customize',
  nextRoute: eGiftCardsEnabled ? Step.EGIFT_CARD : Step.RECIPIENTS,
  previousRoute: null,
  saveOnLeave: true,
  nextButtonText: eGiftCardsEnabled
    ? 'Next: Add an eGift Card (optional)'
    : 'Next: Add recipient(s)',
  hideInWebView: false,
  trackingLabel: 'customize',
};

export const stepMeta: StepMeta = {
  // Not a step - global settings for steps
  editing: false,
  creating: true,
  showBackButton: true,
  countSteps: true,
};

export const steps: Steps = {
  [Step.CREATE]: createStep,
  [Step.CUSTOMIZE]: {
    ...createStep,
    name: Step.CUSTOMIZE,
    path: 'customize',
    displayName: 'Customize',
  },
  [Step.EGIFT_CARD]: {
    id: 2,
    name: Step.EGIFT_CARD,
    path: 'egift-card',
    displayName: 'Add an eGift Card (optional)',
    nextRoute: Step.RECIPIENTS,
    previousRoute: Step.CUSTOMIZE,
    saveOnLeave: true,
    nextButtonText: setGiftCardsNextButtonText,
    nextButtonVariant: setGiftCardsNextButtonVariant,
    preEnterRouteFunction: closeGiftCardDrawer,
    hideInWebView: false,
    trackingLabel: 'optionalGift',
    overrideBottomNav: false,
  },
  [Step.RECIPIENTS]: {
    id: eGiftCardsEnabled ? 3 : 2,
    name: Step.RECIPIENTS,
    path: 'recipients',
    displayName: 'Add recipient(s)',
    nextRoute: Step.RECIPIENTS_MESSAGE,
    previousRoute: eGiftCardsEnabled ? Step.EGIFT_CARD : Step.CUSTOMIZE,
    saveOnLeave: true,
    nextButtonText: 'Next: Add a note (optional)',
    preLeaveRouteFunction: validateRecipientListAndGlobalMessage,
    hideInWebView: false,
    trackingLabel: 'recipients',
    trackEGift: true,
    overrideBottomNav: true,
  },
  [Step.RECIPIENTS_MESSAGE]: {
    id: eGiftCardsEnabled ? 4 : 3,
    path: 'recipients-message',
    name: Step.RECIPIENTS_MESSAGE,
    displayName: 'Add messages',
    nextRoute: Step.REVIEW,
    previousRoute: Step.RECIPIENTS,
    preEnterRouteFunction: resetMessageEditor,
    preLeaveRouteFunction: disableMessageEditor,
    saveOnLeave: true,
    nextButtonText: setRecipientsMessageNextButtonText,
    nextButtonVariant: setRecipientsMessageNextButtonVariant,
    runPreLeaveForPrevious: true,
    hideInWebView: false,
    trackingLabel: 'message',
    trackEGift: true,
    overrideBottomNav: false,
  },
  [Step.REVIEW]: {
    id: eGiftCardsEnabled ? 5 : 4,
    path: 'review',
    name: Step.REVIEW,
    displayName: 'Review & send',
    nextRoute: Step.SUCCESS,
    previousRoute: Step.RECIPIENTS_MESSAGE,
    nextButtonText: 'Purchase & send',
    hideInWebView: true,
    trackingLabel: 'review',
    trackEGift: true,
    overrideBottomNav: false,
    headerCtas: true,
  },
  [Step.SUCCESS]: {
    id: eGiftCardsEnabled ? 6 : 5,
    path: 'success',
    name: Step.SUCCESS,
    displayName: 'Success',
    previousRoute: Step.REVIEW,
    showFooter: true,
    showMegaNav: true,
    trackingLabel: 'complete',
  },
  [Step.EXIT]: {
    // Routes us to the source page
    id: -1,
    path: !document.referrer?.includes?.('login') && document.referrer ? document.referrer : '/',
    hideInWebView: true,
  },
};

export const numSteps = eGiftCardsEnabled ? 5 : 4;

export const getLastPathComponent = (path: string) => {
  const components = path.split('/');
  return components[components.length - 1];
};

export const getCurrentStep = (): StepConfig | null => {
  const {location} = window;
  const lastPathComponent = getLastPathComponent(location.pathname);
  if (!Object.values(Step).includes(lastPathComponent as Step)) return null;
  return steps[lastPathComponent as Step];
};

export const updateEventIdInPathname = (eventId: string) => {
  const components = history.location.pathname.split('/');
  components[1] = eventId;
  if (components[2] === 'create') components[2] = 'customize';
  history.replace(
    {
      pathname: components.join('/'),
      hash: history.location.hash,
      search: history.location.search,
    },
    {}
  );
};

export const updateTemplateNameInPathname = (templateName: string) => {
  const components = history.location.pathname.split('/');
  components[1] = templateName;
  history.replace(
    {
      pathname: components.join('/'),
      hash: history.location.hash,
      search: history.location.search,
    },
    {}
  );
};
